/**
 * Accreditations API client — Session 3.13 (spec-29 v2.1).
 *
 * Mirrors the shapes returned by apps/api/src/services/accreditations.
 */

import type { CapaFoundIn } from '@jeanieiq/types';
import type { MeasureEvery, SupplyType, SupplyUnit } from '@jeanieiq/types';
import { apiFetch, apiUrl, expiredSession, ApiError } from './api';
import { getAccessToken } from './auth';

/* ------------------------------------------------------------------ */
/*  Enums                                                              */
/* ------------------------------------------------------------------ */

export type AccreditationType =
  | 'iso_9001'
  | 'iso_14001'
  | 'iso_27001'
  | 'iso_45001'
  | 'chas'
  | 'chas_standard'
  | 'chas_advanced'
  | 'constructionline'
  | 'constructionline_bronze'
  | 'constructionline_silver'
  | 'constructionline_gold'
  | 'safe_contractor'
  | 'smas'
  | 'altius'
  | 'achilles'
  | 'risqs'
  | 'link_up'
  | 'fors_bronze'
  | 'fors_silver'
  | 'fors_gold'
  | 'niceic'
  | 'elecsa'
  | 'napit'
  | 'gas_safe'
  | 'social_value'
  | 'pqs'
  | 'waste_licence'
  | 'asta'
  | 'other';

export type AccreditationStatus =
  | 'active'
  | 'expired'
  | 'pending_renewal'
  | 'in_audit'
  | 'suspended'
  | 'withdrawn'
  | 'not_held';

export type IsoStandard = 'iso_9001' | 'iso_14001' | 'iso_27001' | 'iso_45001';

/**
 * Display labels for every AccreditationType. Single source of truth — used
 * by AccreditationsPage and the Web Builder (DH-024). Keep in sync with the
 * AccreditationType union above.
 */
export const ACCREDITATION_TYPE_LABELS: Record<AccreditationType, string> = {
  iso_9001: 'ISO 9001:2015 Quality Management',
  iso_14001: 'ISO 14001:2015 Environmental Management',
  iso_27001: 'ISO 27001:2022 Information Security',
  iso_45001: 'ISO 45001:2018 Occupational Health & Safety',
  safe_contractor: 'Safe Contractor',
  chas: 'CHAS',
  chas_standard: 'CHAS Standard',
  chas_advanced: 'CHAS Advanced',
  constructionline: 'Constructionline',
  constructionline_bronze: 'Constructionline Bronze',
  constructionline_silver: 'Constructionline Silver',
  constructionline_gold: 'Constructionline Gold',
  fors_bronze: 'FORS Bronze',
  fors_silver: 'FORS Silver',
  fors_gold: 'FORS Gold',
  gas_safe: 'Gas Safe Register',
  niceic: 'NICEIC',
  elecsa: 'ELECSA',
  napit: 'NAPIT',
  achilles: 'Achilles',
  social_value: 'Social Value',
  pqs: 'PQS',
  waste_licence: 'Waste Carrier Licence',
  smas: 'SMAS',
  altius: 'Altius',
  risqs: 'RISQS',
  link_up: 'Link-Up',
  asta: 'ASTA',
  other: 'Other',
};

/* ------------------------------------------------------------------ */
/*  Public interfaces                                                  */
/* ------------------------------------------------------------------ */

/** One row of the per-scheme renewal checklist (Additional Accreditation tab). */
export interface RenewalChecklistItem {
  label: string;
  done: boolean;
}

export interface AccreditationPublic {
  id: string;
  tenantId: string;
  type: AccreditationType;
  status: AccreditationStatus;
  issuedDate: string | null;
  expiryDate: string | null;
  renewalStartDate: string | null;
  certificateNumber: string | null;
  certifyingBody: string | null;
  certificateS3Key: string | null;
  lastAuditDate: string | null;
  nextAuditDate: string | null;
  auditorName: string | null;
  notes: string | null;
  // Scheme-management layer — populated only by the Additional
  // Accreditation tab for the lighter schemes; null elsewhere.
  membershipNumber: string | null;
  schemeLevel: string | null;
  assessmentStatus: string | null;
  assessmentDate: string | null;
  renewalChecklist: RenewalChecklistItem[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccreditationStats {
  active: number;
  pendingRenewal: number;
  expired: number;
  inAudit: number;
  expiringWithin30Days: number;
}

export interface AccreditationListResult {
  accreditations: AccreditationPublic[];
  stats: AccreditationStats;
}

export interface IsoEvidencePublic {
  id: string;
  tenantId: string;
  standard: string;
  clause: string;
  evidenceType: string;
  sourceModule: string | null;
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  description: string | null;
  recordedById: string | null;
  recordedAt: string;
  autoCollected: boolean;
  evidenceDate: string | null;
  sourceRecordId: string | null;
  s3Key: string | null;
  createdAt: string;
}

export interface EvidenceScoreByStandard {
  score: number;
  clauseCount: number;
}

export interface EvidenceListResult {
  items: IsoEvidencePublic[];
  stats: {
    byStandard: Record<IsoStandard, EvidenceScoreByStandard>;
  };
}

export interface AuditorTokenPublic {
  token: string;
  tenantId: string;
  standard: IsoStandard;
  auditorName: string;
  expiresAt: string;
  lastAccessedAt: string | null;
  ipRestriction: string | null;
  revokedAt: string | null;
  accessCount: number;
  createdAt: string;
}

export interface AuditorTokenListResult {
  active: AuditorTokenPublic[];
  expired: AuditorTokenPublic[];
  revoked: AuditorTokenPublic[];
}

export interface AchieveSubscriptionPublic {
  id: string;
  tenantId: string;
  standard: IsoStandard;
  status: 'active' | 'cancelled' | 'past_due';
  activatedAt: string;
  cancelledAt: string | null;
  orgProfile: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface RoadmapResult {
  standard: IsoStandard;
  progressPercent: number;
  readinessDone: string[];
  readinessNeeded: string[];
  actionItems: string[];
}

export interface IsoDocumentPublic {
  id: string;
  tenantId: string;
  standard: string;
  documentType: string;
  title: string;
  status: 'complete' | 'draft' | 'not_created';
  version: string | null;
  s3Key: string | null;
  content: string | null;
  clause: string | null;
  lastUpdatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentsGroupedResult {
  bms: IsoDocumentPublic[];
  iso_9001: IsoDocumentPublic[];
  iso_14001: IsoDocumentPublic[];
  iso_27001: IsoDocumentPublic[];
  iso_45001: IsoDocumentPublic[];
}

/* ------------------------------------------------------------------ */
/*  Input payloads                                                     */
/* ------------------------------------------------------------------ */

export interface CreateAccreditationPayload {
  type: AccreditationType;
  status?: AccreditationStatus;
  issuedDate?: string | null;
  expiryDate?: string | null;
  renewalStartDate?: string | null;
  certificateNumber?: string | null;
  certifyingBody?: string | null;
  certificateS3Key?: string | null;
  lastAuditDate?: string | null;
  nextAuditDate?: string | null;
  auditorName?: string | null;
  notes?: string | null;
  membershipNumber?: string | null;
  schemeLevel?: string | null;
  assessmentStatus?: string | null;
  assessmentDate?: string | null;
  renewalChecklist?: RenewalChecklistItem[] | null;
}

export type UpdateAccreditationPayload = Partial<CreateAccreditationPayload>;

export interface CreateManualEvidencePayload {
  standard: string;
  clause: string;
  evidenceType: string;
  description?: string | null;
  evidenceDate?: string | null;
  s3Key?: string | null;
}

export interface CreateAuditorTokenPayload {
  standard: IsoStandard;
  auditorName: string;
  expiresInDays: number;
  ipRestriction?: string | null;
}

export interface CreateDocumentPayload {
  standard: string;
  documentType: string;
  title: string;
  status?: 'complete' | 'draft' | 'not_created';
  version?: string | null;
  s3Key?: string | null;
  content?: string | null;
  clause?: string | null;
}

export type UpdateDocumentPayload = Partial<CreateDocumentPayload>;

/* ------------------------------------------------------------------ */
/*  Accreditations (Tab 1)                                             */
/* ------------------------------------------------------------------ */

export async function fetchAccreditations(signal?: AbortSignal): Promise<AccreditationListResult> {
  return apiFetch<AccreditationListResult>('/api/accreditations', { method: 'GET', signal });
}

/**
 * The standards this tenant's sector applies — which ISO boards to show. Empty
 * for a tenant whose sector carries no ISO preset (e.g. pharmacy).
 */
export async function fetchApplicableStandards(
  signal?: AbortSignal,
): Promise<{ standards: IsoStandard[] }> {
  return apiFetch<{ standards: IsoStandard[] }>('/api/accreditations/applicable-standards', {
    method: 'GET',
    signal,
  });
}

/** The company-level Quality-Manual inputs (one per tenant). All fields nullable. */
export interface QmsProfile {
  businessOverview: string | null;
  scopeStatement: string | null;
  scopeExclusions: string | null;
  operatingEnvironment: string | null;
  communicationChannels: string | null;
  processMapNotes: string | null;
  /** BMS 4.4 Part 2, for a company holding ISO 14001. */
  environmentalProcessNotes: string | null;
  managementReviewFrequency: string | null;
  /** The day the review cycle starts and rotates on (YYYY-MM-DD). */
  managementReviewStartOn: string | null;
}
export async function fetchQmsProfile(signal?: AbortSignal): Promise<QmsProfile> {
  return apiFetch<QmsProfile>('/api/accreditations/qms-profile', { method: 'GET', signal });
}
/** `note` says what changed; it is asked once the manual has an approved issue. */
export async function saveQmsProfile(
  input: Partial<QmsProfile> & { note?: string | null },
): Promise<QmsProfile> {
  return apiFetch<QmsProfile>('/api/accreditations/qms-profile', {
    method: 'PUT',
    body: input,
  });
}

/** One issue of a controlled document, and what was revised in it. */
export interface DocumentRevision {
  version: string;
  changeSummary: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  supersededAt: string | null;
  createdAt: string;
}
/** A row of the Master Document List (clause 7.5.3). */
export interface DocumentRegisterRow {
  lastReviewedOn: string | null;
  lastReviewedBy: string | null;
  location: string;
  id: string;
  /** DOC 121 - the register identity. Null on a row the register renders
   *  itself (the Quality Manual, the register document) rather than a
   *  `documents` row. */
  registerNumber?: string | null;
  /** CERT 4 - the number within its category. */
  documentNumber: string | null;
  title: string;
  typeLabel: string;
  owner: string;
  status: string;
  sectionTags: string[];
  nextReviewDate: string | null;
  currentVersion: string | null;
  currentApprovedAt: string | null;
  currentApprovedBy: string | null;
  revisions: DocumentRevision[];
  /** Generated and controlled by the platform (the Quality Manual) rather than
   *  filed as a file — so it is opened in the platform, not downloaded. */
  generated?: boolean;
  /*
   * 2026-08-28. Where the row stands against its approved issue, derived on the
   * server so this list and the document page cannot tell different stories.
   *   not_approved / approved / changed, or null where there is no generated
   *   body for the question to apply to.
   */
  approvalState?: 'not_approved' | 'approved' | 'changed' | null;
}
export async function fetchDocumentRegister(
  signal?: AbortSignal,
): Promise<{ tenantName: string; documents: DocumentRegisterRow[] }> {
  return apiFetch<{ tenantName: string; documents: DocumentRegisterRow[] }>('/api/accreditations/document-register', {
    method: 'GET',
    signal,
  });
}

/** One section of the assembled Quality Manual. */
export interface ManualSection {
  number: string;
  title: string;
  /** fixed = same for everyone; profile = the tenant's own words; missing = no
   *  answer yet, `hint` says where to give one; edited = worded by hand. */
  source: 'fixed' | 'profile' | 'missing' | 'edited';
  body: string[];
  items?: string[];
  hint?: string;
  /** Edited by hand — overrides the generated wording. */
  edited?: boolean;
  /** What the platform would say if the override were removed. */
  generatedBody?: string[];
  /** The stored answers this clause is written from; set only on the company-detail clauses. */
  answer?: string[];
  /** Printed with part of its answer still to be added. */
  incomplete?: boolean;
}
export interface ManualAmendment {
  issue: string;
  date: string | null;
  change: string;
  initials: string | null;
}
export interface ManualControl {
  docNumber: string;
  issue: string;
  approvedBy: string | null;
  approvedOn: string | null;
  approvedPosition: string | null;
  amendments: ManualAmendment[];
}
/** The BMS (ISO 9001, integrated with ISO 14001 when kept as one) or the separate ISO 14001 manual. */
export type ManualKey = 'iso_9001' | 'iso_14001';
export type ManualMode = 'integrated' | 'separate';
export interface QualityManualResult {
  tenantName: string;
  standard: ManualKey;
  /** The standards this manual covers: both for the integrated BMS. */
  standards: ManualKey[];
  mode: ManualMode;
  /** The company holds ISO 9001 and ISO 14001, so it can keep a manual for each. */
  canSeparate: boolean;
  generatedAt: string;
  control: ManualControl;
  completeness: { answered: number; total: number };
  /** The working copy: the approved issue with any changes saved since. */
  sections: ManualSection[];
  /** The latest issue as approved; null before a first approval, or for an
   *  issue approved before each issue's wording was kept. */
  approvedSections: ManualSection[] | null;
  /** Saved changes waiting for approval in the Document Register. */
  pending: ManualPendingChange[];
}
export interface ManualPendingChange {
  sectionNumber: string;
  note: string;
  changedBy: string;
  changedOn: string;
}
export async function fetchQualityManual(
  signal?: AbortSignal,
  manual: ManualKey = 'iso_9001',
): Promise<QualityManualResult> {
  return apiFetch<QualityManualResult>('/api/accreditations/quality-manual', {
    method: 'GET',
    query: { manual },
    signal,
  });
}
/** One integrated BMS or a manual for each standard (a company holding ISO 9001 and ISO 14001). */
export async function setManualMode(
  mode: ManualMode,
): Promise<{ mode: ManualMode; environmentalManualDocNumber: string | null }> {
  return apiFetch('/api/accreditations/quality-manual/mode', { method: 'PUT', body: { mode } });
}
/** Override a section's wording. Revert to bring the generated text back. */
export async function saveManualSection(
  sectionNumber: string,
  input: { title?: string | null; body: string; note?: string | null; manual?: ManualKey },
): Promise<{ sectionNumber: string }> {
  return apiFetch<{ sectionNumber: string }>(
    `/api/accreditations/quality-manual/sections/${encodeURIComponent(sectionNumber)}`,
    { method: 'PUT', body: input },
  );
}
export async function approveDocumentRegister(
  position: string | null,
): Promise<{ approvedBy: string; approvedOn: string; approvedPosition: string | null }> {
  return apiFetch('/api/accreditations/document-register/approve', {
    method: 'POST',
    body: { position },
  });
}
export async function approveQualityManual(
  position: string | null,
  manual: ManualKey = 'iso_9001',
): Promise<ManualControl> {
  return apiFetch<ManualControl>('/api/accreditations/quality-manual/approve', {
    method: 'POST',
    body: { position, manual },
  });
}
export async function revertManualSection(
  sectionNumber: string,
  note: string | null,
  manual: ManualKey = 'iso_9001',
): Promise<{ sectionNumber: string }> {
  return apiFetch<{ sectionNumber: string }>(
    `/api/accreditations/quality-manual/sections/${encodeURIComponent(sectionNumber)}`,
    { method: 'DELETE', body: { note, manual } },
  );
}

export type LegalServes = 'quality' | 'environment' | 'both';
export type LegalEvaluationResult = 'compliant' | 'partly' | 'not_compliant';

export interface LegalEvaluation {
  id: string;
  evaluatedOn: string;
  result: LegalEvaluationResult;
  note: string;
  evaluatedById: string;
}

/** A legal or regulatory obligation — ISO 9001 clause 4.1; ISO 14001 6.1.3 when it serves the environment. */
export interface LegalRegisterEntry {
  id: string;
  title: string;
  citation: string | null;
  url: string | null;
  category: string | null;
  appliesTo: string | null;
  complianceEvaluation: string | null;
  /** A role, never a named person. */
  responsibleRole: string | null;
  lastReviewedOn: string | null;
  serves: LegalServes;
  evaluateEveryMonths: number | null;
  /** Newest first. */
  evaluations: LegalEvaluation[];
  /** The server's: the last day the next evaluation is in date. */
  nextEvaluationDue: string | null;
  evaluationOverdue: boolean;
  /** The day it was added to the register. */
  addedOn: string;
  /** The day the obligation itself last changed; recording a review does not move it. */
  changedOn: string | null;
  /** The day it was retired; null while it applies. */
  retiredOn: string | null;
}
export type LegalRegisterEntryInput = Omit<
  LegalRegisterEntry,
  'id' | 'evaluations' | 'nextEvaluationDue' | 'evaluationOverdue' | 'addedOn' | 'changedOn' | 'retiredOn'
>;
export interface LegalEvaluationPayload {
  evaluatedOn: string;
  result: LegalEvaluationResult;
  note: string;
}

/** The register; with retired, the obligations retired from it, each with its retired day. */
export async function fetchLegalRegister(signal?: AbortSignal, retired = false): Promise<LegalRegisterEntry[]> {
  return apiFetch<LegalRegisterEntry[]>('/api/accreditations/legal-register', {
    method: 'GET',
    ...(retired ? { query: { retired: '1' } } : {}),
    signal,
  });
}
export async function createLegalRegisterEntry(
  input: LegalRegisterEntryInput,
): Promise<LegalRegisterEntry> {
  return apiFetch<LegalRegisterEntry>('/api/accreditations/legal-register', {
    method: 'POST',
    body: input,
  });
}
export async function updateLegalRegisterEntry(
  id: string,
  input: LegalRegisterEntryInput,
): Promise<LegalRegisterEntry> {
  return apiFetch<LegalRegisterEntry>(`/api/accreditations/legal-register/${id}`, {
    method: 'PUT',
    body: input,
  });
}
export async function retireLegalRegisterEntry(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/api/accreditations/legal-register/${id}`, {
    method: 'DELETE',
  });
}
/** An entry that serves the environment files ISO 14001 9.1.2 and 6.1.3 on the server. */
export async function recordLegalEvaluation(
  id: string,
  payload: LegalEvaluationPayload,
): Promise<LegalRegisterEntry> {
  return apiFetch<LegalRegisterEntry>(`/api/accreditations/legal-register/${id}/evaluations`, {
    method: 'POST',
    body: payload,
  });
}

export async function createAccreditation(
  payload: CreateAccreditationPayload,
): Promise<AccreditationPublic> {
  return apiFetch<AccreditationPublic>('/api/accreditations', {
    method: 'POST',
    body: payload,
  });
}

/**
 * Certificate-vault bridge ONLY. Files a cert as the accreditation of a
 * type, keeping one current Accreditation register row per type
 * (creates if none, updates the latest, soft-deletes same-type dupes
 * server-side). Same payload as create.
 */
export async function supersedeAccreditationFromCert(
  payload: CreateAccreditationPayload,
): Promise<AccreditationPublic> {
  return apiFetch<AccreditationPublic>('/api/accreditations/from-cert', {
    method: 'POST',
    body: payload,
  });
}

export async function updateAccreditation(
  id: string,
  payload: UpdateAccreditationPayload,
): Promise<AccreditationPublic> {
  return apiFetch<AccreditationPublic>(`/api/accreditations/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function deleteAccreditation(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/accreditations/${id}`, { method: 'DELETE' });
}

/* ------------------------------------------------------------------ */
/*  Evidence (Tab 2)                                                   */
/* ------------------------------------------------------------------ */

export async function fetchEvidence(
  opts: { standard?: string; clause?: string; page?: number } = {},
  signal?: AbortSignal,
): Promise<EvidenceListResult> {
  return apiFetch<EvidenceListResult>('/api/accreditations/evidence', {
    method: 'GET',
    query: {
      standard: opts.standard || null,
      clause: opts.clause || null,
      page: opts.page ? String(opts.page) : null,
    },
    signal,
  });
}

/**
 * Audit Review pack — pull EVERY evidence record for a standard by
 * paging the existing endpoint (server pages at 50; no API change).
 * Stops when a short page is returned; hard-capped to avoid a runaway
 * loop. Stats come back accurate on any page (computed server-side
 * over all rows) so the caller can read page 1's stats if needed.
 */
export async function fetchAllEvidence(
  standard: string,
  signal?: AbortSignal,
): Promise<IsoEvidencePublic[]> {
  const all: IsoEvidencePublic[] = [];
  for (let page = 1; page <= 200; page += 1) {
    const res = await fetchEvidence({ standard, page }, signal);
    all.push(...res.items);
    if (res.items.length < 50) break;
  }
  return all;
}

/**
 * Every evidence record for the clause map, not just the first page.
 *
 * The server pages at 50 and computes its stats over ALL rows, so page 1's
 * stats are already right — but the clause map counts distinct clauses from
 * the ITEMS, and a tenant past 50 records would silently lose clauses off the
 * end. ISO-EVIDENCE-WRITE-READ-SPLIT-001, 2026-08-21.
 */
export async function fetchEvidenceAllPages(
  opts: { standard?: string; clause?: string } = {},
  signal?: AbortSignal,
): Promise<EvidenceListResult> {
  const first = await fetchEvidence({ ...opts, page: 1 }, signal);
  const items = [...first.items];
  if (first.items.length === 50) {
    for (let page = 2; page <= 200; page += 1) {
      const next = await fetchEvidence({ ...opts, page }, signal);
      items.push(...next.items);
      if (next.items.length < 50) break;
    }
  }
  return { items, stats: first.stats };
}

export async function createManualEvidence(
  payload: CreateManualEvidencePayload,
): Promise<IsoEvidencePublic> {
  return apiFetch<IsoEvidencePublic>('/api/accreditations/evidence', {
    method: 'POST',
    body: payload,
  });
}

// ─── Slice DR-003 — Register-backed Evidence + Documents readers ──────
//
// Mirror of fetchEvidence + fetchAccreditationDocuments but sourced from
// the central Document Register. The G.1 + G.3 backfills moved every
// legacy `iso_evidence` row and every legacy `iso_document` row into
// the Register as REC-/POL-/PRC-/MAN- documents, preserving the original
// metadata in `notes` behind migration markers. These readers parse the
// markers + structured notes and reshape back to the legacy shapes so
// the existing tables in AccreditationsPage render unchanged.
//
// Same pattern as the Company Vault G.5 cutover. Write-side stays on
// the legacy /api/accreditations/{evidence,documents} endpoints for now
// — the UI doesn't currently expose a write path that uses them; modern
// uploads land via EntityDocumentsPanel on the accreditation itself.

import {
  fetchDocuments,
  type DocumentSummary,
} from './documentRegisterApi';

const ISO_EVIDENCE_MARKER = /\[migrated from iso_evidence:([0-9a-f-]+)\]/i;
const ISO_DOCUMENT_MARKER = /\[migrated from iso_document:([0-9a-f-]+)\]/i;
const STANDARD_LINE = /^Standard:\s*(.+)$/im;
const CLAUSE_LINE = /^Clause:\s*(.+)$/im;
const EVIDENCE_TYPE_LINE = /^Evidence type:\s*(.+)$/im;
const SOURCE_MODULE_LINE = /^Source module:\s*(.+)$/im;
const RELATED_ENTITY_LINE = /^Related entity:\s*([^/\s]+)\s*\/\s*([^/\s]+)$/im;
const RECORDED_AT_LINE = /^Recorded at:\s*(.+)$/im;
const EVIDENCE_DATE_LINE = /^Evidence date:\s*(\d{4}-\d{2}-\d{2})/im;
const FULL_DESC_LINE = /Full description:\s*\n([\s\S]*)$/i;
const LEGACY_DOC_TYPE_LINE = /^Legacy documentType:\s*(.+)$/im;
const LEGACY_VERSION_LINE = /^Legacy version:\s*(.+)$/im;
const LEGACY_STATUS_LINE = /^Legacy status:\s*(.+)$/im;
const LAST_UPDATED_LINE = /^Last updated:\s*(.+)$/im;

function valueOrNull(s: string | undefined): string | null {
  if (!s) return null;
  const t = s.trim();
  if (!t || t === '—' || t === 'null' || t === 'undefined') return null;
  return t;
}

function shapeIsoEvidence(
  d: DocumentSummary,
  legacyId: string,
): IsoEvidencePublic | null {
  const notes = d.notes ?? '';
  const standard = notes.match(STANDARD_LINE)?.[1]?.trim();
  const clause = notes.match(CLAUSE_LINE)?.[1]?.trim();
  const evidenceType = notes.match(EVIDENCE_TYPE_LINE)?.[1]?.trim();
  if (!standard || !clause || !evidenceType) return null;
  const sourceModule = valueOrNull(notes.match(SOURCE_MODULE_LINE)?.[1]);
  const relMatch = notes.match(RELATED_ENTITY_LINE);
  const relatedEntityType = valueOrNull(relMatch?.[1]);
  const relatedEntityId = valueOrNull(relMatch?.[2]);
  const recordedAtRaw = notes.match(RECORDED_AT_LINE)?.[1]?.trim();
  const evidenceDate = notes.match(EVIDENCE_DATE_LINE)?.[1] ?? null;
  const fullDesc = valueOrNull(notes.match(FULL_DESC_LINE)?.[1]);
  return {
    id: legacyId,
    tenantId: '',
    standard,
    clause,
    evidenceType,
    sourceModule,
    relatedEntityType,
    relatedEntityId,
    description: fullDesc,
    recordedById: null,
    recordedAt: recordedAtRaw ?? d.createdAt,
    autoCollected: sourceModule !== null && sourceModule !== 'manual',
    evidenceDate,
    sourceRecordId: null,
    s3Key: null,
    createdAt: d.createdAt,
  };
}

/**
 * Reshape one register row into the legacy `IsoDocumentPublic` the ISO Document
 * Vault renders.
 *
 * TAKES BOTH KINDS OF DOCUMENT SINCE 2026-08-25. Shane: *"sort that so the docs
 * show please"*.
 *
 * It used to handle only rows the DR-003 backfill had migrated, and it dropped
 * everything else — `if (!standard) return null`, where `standard` was parsed out
 * of a `Standard:` line the backfill wrote into `notes`. Combined with the marker
 * check in the caller, that meant a document created fresh in the register never
 * appeared in this tab however well it was filled in. The tab was a view of a
 * one-off migration rather than of the register.
 *
 * `legacyId` is null for a document that was never migrated, and then the
 * register row's own id is used. Nothing downstream cares which it is; the tab
 * only needs a stable key.
 *
 * WHERE THE MISSING FACTS COME FROM for a fresh document, none of which is a
 * guess:
 *
 *   standard   -> 'bms'. There is no standard column on `documents`, and the bms
 *                 bucket already exists for exactly this: the backfill's own
 *                 comment calls it "tenant-level management-system docs that
 *                 aren't tied to a specific ISO standard". Inventing iso_9001
 *                 would file a document under a standard nobody chose.
 *   status     -> from the register's own lifecycle. Only `approved` reads as
 *                 'complete'; draft, under_review, superseded and withdrawn all
 *                 read as 'draft'. A superseded document is emphatically not
 *                 complete, and calling it so would overstate the vault.
 *   version    -> the current approved version, or null before the first one.
 *   updated    -> the row's own updatedAt.
 *
 * Migrated rows keep reading their legacy metadata first, so nothing about their
 * display changes.
 *
 * `clause` COMES FROM THE COLUMN NOW (2026-08-26), with the `Clause:` notes line
 * kept only as a fallback. `documents.clause` (20260825190000) is surfaced on
 * `DocumentSummary`, so the compliance-relevant value no longer depends on
 * parsing a free-text box a user can edit.
 *
 * The notes fallback stays for one reason worth stating rather than assuming: the
 * migration's backfill deliberately refused any `Clause:` value that was not
 * dotted digits, so a row whose note reads "Clause: see appendix B" has a NULL
 * column and its note is the only record of the intent. Dropping the fallback
 * would blank those on screen. It is display-only — nothing decides evidence from
 * it.
 */
function shapeIsoDocument(
  d: DocumentSummary,
  legacyId: string | null,
): IsoDocumentPublic {
  const notes = d.notes ?? '';
  const standard = notes.match(STANDARD_LINE)?.[1]?.trim() ?? 'bms';
  const documentType =
    notes.match(LEGACY_DOC_TYPE_LINE)?.[1]?.trim() ?? d.typeCode;
  const clause = d.clause ?? valueOrNull(notes.match(CLAUSE_LINE)?.[1]);
  const version =
    valueOrNull(notes.match(LEGACY_VERSION_LINE)?.[1]) ?? d.currentVersion;
  const legacyStatus = notes.match(LEGACY_STATUS_LINE)?.[1]?.trim();
  const status: IsoDocumentPublic['status'] = legacyStatus
    ? legacyStatus === 'complete'
      ? 'complete'
      : legacyStatus === 'not_created'
        ? 'not_created'
        : 'draft'
    : d.status === 'approved'
      ? 'complete'
      : 'draft';
  const lastUpdatedRaw = notes.match(LAST_UPDATED_LINE)?.[1]?.trim();
  const lastUpdatedAt =
    lastUpdatedRaw && lastUpdatedRaw !== '—' ? lastUpdatedRaw : d.updatedAt;
  return {
    id: legacyId ?? d.id,
    tenantId: '',
    standard,
    documentType,
    title: d.title,
    status,
    version,
    s3Key: null,
    content: null,
    clause,
    lastUpdatedAt,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
  };
}

/**
 * Read-side cutover for `fetchEvidence`. Pulls every Register REC
 * document carrying the `[migrated from iso_evidence:UUID]` marker,
 * reshapes back to IsoEvidencePublic, computes the same byStandard
 * stats client-side.
 *
 * Filters (`standard`, `clause`) apply to the returned items but NOT
 * to the stats — stats stay tenant-wide so the dashboard score
 * doesn't change when the user filters the table.
 */
export async function fetchEvidenceFromRegister(
  opts: { standard?: string; clause?: string; page?: number } = {},
  signal?: AbortSignal,
): Promise<EvidenceListResult> {
  const docs = await fetchDocuments({ section: 'quality' }, signal);
  const all: IsoEvidencePublic[] = [];
  for (const d of docs) {
    const m = (d.notes ?? '').match(ISO_EVIDENCE_MARKER);
    if (!m?.[1]) continue;
    const shaped = shapeIsoEvidence(d, m[1]);
    if (shaped) all.push(shaped);
  }
  // Tenant-wide byStandard stats (independent of the filters).
  const byStandard: Record<IsoStandard, EvidenceScoreByStandard> = {
    iso_9001: { score: 0, clauseCount: 0 },
    iso_14001: { score: 0, clauseCount: 0 },
    iso_27001: { score: 0, clauseCount: 0 },
    iso_45001: { score: 0, clauseCount: 0 },
  };
  const clausesByStd: Record<string, Set<string>> = {};
  for (const ev of all) {
    if (ev.standard in byStandard) {
      const s = ev.standard as IsoStandard;
      clausesByStd[s] = clausesByStd[s] ?? new Set();
      clausesByStd[s]!.add(ev.clause);
    }
  }
  for (const s of Object.keys(byStandard) as IsoStandard[]) {
    const set = clausesByStd[s];
    const count = set ? set.size : 0;
    byStandard[s] = {
      clauseCount: count,
      // Same simplification as the legacy server-side calc when no
      // clause registry is loaded: presence is the score. The dashboard
      // surface treats "score" as "have we collected any evidence" not
      // a precise percentage — sufficient for the cutover. Future
      // refinement: load the per-standard clause registry and compute
      // a real ratio.
      score: count > 0 ? Math.min(100, count * 10) : 0,
    };
  }
  // Apply the request filters to the items returned.
  let items = all;
  if (opts.standard) items = items.filter((i) => i.standard === opts.standard);
  if (opts.clause) items = items.filter((i) => i.clause === opts.clause);
  return { items, stats: { byStandard } };
}

/**
 * Read-side cutover for `fetchAccreditationDocuments`. Pulls every
 * Register POL/PRC/MAN/REC document carrying the
 * `[migrated from iso_document:UUID]` marker, reshapes back to
 * IsoDocumentPublic grouped by standard.
 *
 * BMS docs land in the 'bms' bucket on the legacy shape; the backfill
 * uses 'bms' as the Standard for tenant-level management-system docs
 * that aren't tied to a specific ISO standard.
 */
export async function fetchAccreditationDocumentsFromRegister(
  signal?: AbortSignal,
): Promise<DocumentsGroupedResult> {
  const docs = await fetchDocuments({ section: 'quality' }, signal);
  const grouped: DocumentsGroupedResult = {
    bms: [],
    iso_9001: [],
    iso_14001: [],
    iso_27001: [],
    iso_45001: [],
  };
  for (const d of docs) {
    /*
     * EVERY quality-tagged document, not only the migrated ones.
     *
     * This used to be `if (!m?.[1]) continue` — skip anything without the DR-003
     * marker — which silently hid every document created in the register after
     * the cutover. A tenant could add a controlled document, tag it Quality,
     * approve it, and never see it here.
     *
     * The marker is still read, because a migrated row's id has to stay the
     * legacy one: the tab's "not yet created" links and any bookmarked id were
     * minted against it, and swapping to the register id would break them.
     */
    const legacyId = (d.notes ?? '').match(ISO_DOCUMENT_MARKER)?.[1] ?? null;
    const shaped = shapeIsoDocument(d, legacyId);
    const key = shaped.standard as keyof DocumentsGroupedResult;
    if (key in grouped) {
      grouped[key].push(shaped);
    } else {
      // Anything whose Standard: line is not one of the five buckets — including
      // 'bms'/'BMS' and the default applied to fresh documents — lands in BMS
      // rather than being dropped on the floor, which is what happened before.
      grouped.bms.push(shaped);
    }
  }
  return grouped;
}

/* ------------------------------------------------------------------ */
/*  Auditor tokens (Tab 8)                                             */
/* ------------------------------------------------------------------ */

export async function fetchAuditorTokens(signal?: AbortSignal): Promise<AuditorTokenListResult> {
  return apiFetch<AuditorTokenListResult>('/api/accreditations/auditor-access', {
    method: 'GET',
    signal,
  });
}

export async function createAuditorToken(
  payload: CreateAuditorTokenPayload,
): Promise<AuditorTokenPublic> {
  return apiFetch<AuditorTokenPublic>('/api/accreditations/auditor-access', {
    method: 'POST',
    body: payload,
  });
}

export async function revokeAuditorToken(token: string): Promise<void> {
  await apiFetch<unknown>(`/api/accreditations/auditor-access/${encodeURIComponent(token)}`, {
    method: 'DELETE',
  });
}

/* ------------------------------------------------------------------ */
/*  Achieve (Tab 4)                                                    */
/* ------------------------------------------------------------------ */

export async function fetchAchieveSubscriptions(
  signal?: AbortSignal,
): Promise<AchieveSubscriptionPublic[]> {
  return apiFetch<AchieveSubscriptionPublic[]>('/api/accreditations/achieve/subscription', {
    method: 'GET',
    signal,
  });
}

export async function subscribe(standard: IsoStandard): Promise<AchieveSubscriptionPublic> {
  return apiFetch<AchieveSubscriptionPublic>('/api/accreditations/achieve/subscribe', {
    method: 'POST',
    body: { standard },
  });
}

export async function cancelSubscription(
  standard: IsoStandard,
): Promise<AchieveSubscriptionPublic> {
  return apiFetch<AchieveSubscriptionPublic>('/api/accreditations/achieve/cancel', {
    method: 'POST',
    body: { standard },
  });
}

export async function saveOrgProfile(
  standard: IsoStandard,
  profile: Record<string, unknown>,
): Promise<AchieveSubscriptionPublic> {
  return apiFetch<AchieveSubscriptionPublic>('/api/accreditations/achieve/profile', {
    method: 'POST',
    body: { standard, profile },
  });
}

export async function fetchRoadmap(standard: IsoStandard): Promise<RoadmapResult> {
  return apiFetch<RoadmapResult>(
    `/api/accreditations/achieve/roadmap/${encodeURIComponent(standard)}`,
    { method: 'GET' },
  );
}

/* ------------------------------------------------------------------ */
/*  ISO Documents (Tab 7)                                              */
/* ------------------------------------------------------------------ */

export async function fetchAccreditationDocuments(
  signal?: AbortSignal,
): Promise<DocumentsGroupedResult> {
  return apiFetch<DocumentsGroupedResult>('/api/accreditations/documents', {
    method: 'GET',
    signal,
  });
}

export async function createAccreditationDocument(
  payload: CreateDocumentPayload,
): Promise<IsoDocumentPublic> {
  return apiFetch<IsoDocumentPublic>('/api/accreditations/documents', {
    method: 'POST',
    body: payload,
  });
}

export async function updateAccreditationDocument(
  id: string,
  payload: UpdateDocumentPayload,
): Promise<IsoDocumentPublic> {
  return apiFetch<IsoDocumentPublic>(`/api/accreditations/documents/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

/* ------------------------------------------------------------------ */
/*  ISO 9.3 — Management Review                                         */
/* ------------------------------------------------------------------ */

export interface ManagementReviewAction {
  id: string;
  reviewId: string;
  description: string;
  owner: string | null;
  dueDate: string | null;
  status: string;
  completedAt: string | null;
  createdAt: string;
}
/** An agenda item's responsible person and target date (ISO 14001 and integrated agendas). */
export interface ReviewAgendaEntry {
  responsible: string | null;
  targetDate: string | null;
}
export interface ManagementReview {
  id: string;
  title: string;
  /** Null for an integrated review; the one standard otherwise. */
  standard: 'iso_9001' | 'iso_14001' | null;
  /** The standards the review covers, which choose its agenda. */
  covers: string[];
  agenda: Record<string, ReviewAgendaEntry>;
  nextMeetingOn: string | null;
  reviewDate: string;
  periodLabel: string | null;
  chairName: string | null;
  attendees: string | null;
  /** The employees who attended, picked from the employee list. */
  attendeeEmployees: { employeeId: string; name: string; left: boolean }[];
  inputs: Record<string, string>;
  outputs: string | null;
  status: string;
  finalisedAt: string | null;
  createdAt: string;
  actions: ManagementReviewAction[];
  priorOpenActions: ManagementReviewAction[];
}
export interface ManagementReviewListItem {
  id: string;
  title: string;
  standard: 'iso_9001' | 'iso_14001' | null;
  reviewDate: string;
  periodLabel: string | null;
  status: string;
  finalisedAt: string | null;
  openActionCount: number;
  totalActionCount: number;
}

export async function fetchManagementReviews(
  signal?: AbortSignal,
): Promise<ManagementReviewListItem[]> {
  return apiFetch<ManagementReviewListItem[]>(
    '/api/accreditations/management-reviews',
    { method: 'GET', signal },
  );
}

export async function fetchManagementReview(
  id: string,
  signal?: AbortSignal,
): Promise<ManagementReview> {
  return apiFetch<ManagementReview>(
    `/api/accreditations/management-reviews/${id}`,
    { method: 'GET', signal },
  );
}

export async function createManagementReview(payload: {
  title: string;
  reviewDate: string;
  periodLabel?: string | null;
  chairName?: string | null;
  attendees?: string | null;
  attendeeEmployeeIds?: string[];
  standard?: 'iso_9001' | 'iso_14001' | null;
}): Promise<ManagementReview> {
  return apiFetch<ManagementReview>(
    '/api/accreditations/management-reviews',
    { method: 'POST', body: payload },
  );
}

export async function updateManagementReview(
  id: string,
  payload: {
    title?: string;
    reviewDate?: string;
    periodLabel?: string | null;
    chairName?: string | null;
    attendees?: string | null;
    attendeeEmployeeIds?: string[];
    inputs?: Record<string, string>;
    agenda?: Record<string, ReviewAgendaEntry>;
    nextMeetingOn?: string | null;
    outputs?: string | null;
  },
): Promise<ManagementReview> {
  return apiFetch<ManagementReview>(
    `/api/accreditations/management-reviews/${id}`,
    { method: 'PATCH', body: payload },
  );
}

export async function finaliseManagementReview(
  id: string,
): Promise<ManagementReview> {
  return apiFetch<ManagementReview>(
    `/api/accreditations/management-reviews/${id}/finalise`,
    { method: 'POST', body: {} },
  );
}

export async function addReviewAction(
  reviewId: string,
  payload: { description: string; owner?: string | null; dueDate?: string | null },
): Promise<ManagementReviewAction> {
  return apiFetch<ManagementReviewAction>(
    `/api/accreditations/management-reviews/${reviewId}/actions`,
    { method: 'POST', body: payload },
  );
}

export async function updateReviewAction(
  actionId: string,
  payload: {
    description?: string;
    owner?: string | null;
    dueDate?: string | null;
    status?: 'open' | 'done';
  },
): Promise<ManagementReviewAction> {
  return apiFetch<ManagementReviewAction>(
    `/api/accreditations/management-review-actions/${actionId}`,
    { method: 'PATCH', body: payload },
  );
}

/* ------------------------------------------------------------------ */
/*  ISO 9.2 — Internal Audit                                            */
/* ------------------------------------------------------------------ */

export type FindingType = 'nonconformity' | 'observation' | 'improvement';

export interface InternalAuditFinding {
  id: string;
  auditId: string;
  description: string;
  findingType: string;
  owner: string | null;
  dueDate: string | null;
  status: string;
  completedAt: string | null;
  createdAt: string;
  /** A nonconformity's corrective action: it closes and reopens with it. */
  capaId: string | null;
  /** Read from that corrective action. */
  rootCause: string | null;
  effectivenessReview: string | null;
  effectivenessAt: string | null;
}
/** What completing an audit needs (ISO 9001 9.2.2). */
export type InternalAuditPart = 'auditor' | 'independence' | 'scope' | 'criteria' | 'summary';
export interface InternalAudit {
  id: string;
  reference: string | null;
  documentationReference: string | null;
  auditees: string | null;
  /** The area manager's signature, after the auditor's. */
  authorisedBy: string | null;
  authorisedAt: string | null;
  title: string;
  auditDate: string;
  programmeLabel: string | null;
  auditedClause: string | null;
  auditedArea: string | null;
  auditorName: string | null;
  auditorEmployeeId: string | null;
  auditor: ObjectivePerson;
  auditorIndependenceNote: string | null;
  /** Null for an audit of every standard held; the standard where each is audited apart. */
  standard: 'iso_9001' | 'iso_14001' | null;
  /** What completing still needs; empty when it can be completed. The server decides. */
  completeMissing: InternalAuditPart[];
  scope: string | null;
  criteria: string | null;
  summary: string | null;
  status: string;
  completedAt: string | null;
  createdAt: string;
  findings: InternalAuditFinding[];
  priorOpenFindings: InternalAuditFinding[];
}
export interface InternalAuditListItem {
  id: string;
  reference: string | null;
  auditees: string | null;
  authorisedBy: string | null;
  authorisedAt: string | null;
  title: string;
  auditDate: string;
  programmeLabel: string | null;
  auditedClause: string | null;
  auditedArea: string | null;
  auditorName: string | null;
  standard: 'iso_9001' | 'iso_14001' | null;
  status: string;
  completedAt: string | null;
  openFindingCount: number;
  totalFindingCount: number;
}

export async function fetchInternalAudits(
  signal?: AbortSignal,
): Promise<InternalAuditListItem[]> {
  return apiFetch<InternalAuditListItem[]>(
    '/api/accreditations/internal-audits',
    { method: 'GET', signal },
  );
}

export async function fetchInternalAudit(
  id: string,
  signal?: AbortSignal,
): Promise<InternalAudit> {
  return apiFetch<InternalAudit>(
    `/api/accreditations/internal-audits/${id}`,
    { method: 'GET', signal },
  );
}

export async function createInternalAudit(payload: {
  title: string;
  auditDate: string;
  programmeLabel?: string | null;
  auditedClause?: string | null;
  auditedArea?: string | null;
  auditorName?: string | null;
  auditorEmployeeId?: string | null;
  auditorIndependenceNote?: string | null;
  scope?: string | null;
  criteria?: string | null;
  documentationReference?: string | null;
  auditees?: string | null;
  standard?: 'iso_9001' | 'iso_14001' | null;
}): Promise<InternalAudit> {
  return apiFetch<InternalAudit>(
    '/api/accreditations/internal-audits',
    { method: 'POST', body: payload },
  );
}

export async function updateInternalAudit(
  id: string,
  payload: {
    title?: string;
    auditDate?: string;
    programmeLabel?: string | null;
    auditedClause?: string | null;
    auditedArea?: string | null;
    auditorName?: string | null;
    auditorEmployeeId?: string | null;
    auditorIndependenceNote?: string | null;
    scope?: string | null;
    criteria?: string | null;
    summary?: string | null;
    documentationReference?: string | null;
    auditees?: string | null;
  },
): Promise<InternalAudit> {
  return apiFetch<InternalAudit>(
    `/api/accreditations/internal-audits/${id}`,
    { method: 'PATCH', body: payload },
  );
}

export async function authoriseInternalAudit(id: string): Promise<InternalAudit> {
  return apiFetch<InternalAudit>(`/api/accreditations/internal-audits/${id}/authorise`, { method: 'POST', body: {} });
}

export async function completeInternalAudit(
  id: string,
): Promise<InternalAudit> {
  return apiFetch<InternalAudit>(
    `/api/accreditations/internal-audits/${id}/complete`,
    { method: 'POST', body: {} },
  );
}

export async function addAuditFinding(
  auditId: string,
  payload: {
    description: string;
    findingType?: FindingType;
    owner?: string | null;
    dueDate?: string | null;
  },
): Promise<InternalAuditFinding> {
  return apiFetch<InternalAuditFinding>(
    `/api/accreditations/internal-audits/${auditId}/findings`,
    { method: 'POST', body: payload },
  );
}

export async function updateAuditFinding(
  findingId: string,
  payload: {
    description?: string;
    findingType?: FindingType;
    owner?: string | null;
    dueDate?: string | null;
    status?: 'open' | 'closed';
  },
): Promise<InternalAuditFinding> {
  return apiFetch<InternalAuditFinding>(
    `/api/accreditations/internal-audit-findings/${findingId}`,
    { method: 'PATCH', body: payload },
  );
}

/* ------------------------------------------------------------------ */
/*  ISO 9.2 — Annual Audit Programme                                    */
/* ------------------------------------------------------------------ */

export interface AuditProgrammeAuditItem {
  id: string;
  reference: string | null;
  title: string;
  auditDate: string;
  auditedClause: string | null;
  auditedArea: string | null;
  auditorName: string | null;
  standard: 'iso_9001' | 'iso_14001' | null;
  status: string;
  openFindingCount: number;
  totalFindingCount: number;
}
export interface AuditProgramme {
  id: string;
  year: number;
  status: string;
  notes: string | null;
  /** Schedule groups not audited this year, as "<schedule>:<group>". */
  notApplicableGroups: string[];
  closedAt: string | null;
  createdAt: string;
  audits: AuditProgrammeAuditItem[];
}
export interface AuditProgrammeListItem {
  id: string;
  year: number;
  status: string;
  /** The one the company is working in, which the page opens on. */
  current: boolean;
  notes: string | null;
  closedAt: string | null;
  auditCount: number;
  completedAuditCount: number;
}

export async function fetchAuditProgrammes(
  signal?: AbortSignal,
): Promise<AuditProgrammeListItem[]> {
  return apiFetch<AuditProgrammeListItem[]>(
    '/api/accreditations/audit-programmes',
    { method: 'GET', signal },
  );
}

export async function fetchCurrentAuditProgramme(
  signal?: AbortSignal,
): Promise<AuditProgramme | null> {
  return apiFetch<AuditProgramme | null>(
    '/api/accreditations/audit-programmes/current',
    { method: 'GET', signal },
  );
}

export async function fetchAuditProgramme(
  id: string,
  signal?: AbortSignal,
): Promise<AuditProgramme> {
  return apiFetch<AuditProgramme>(
    `/api/accreditations/audit-programmes/${id}`,
    { method: 'GET', signal },
  );
}

export async function startAuditProgramme(payload: {
  year?: number;
  notes?: string | null;
}): Promise<AuditProgramme> {
  return apiFetch<AuditProgramme>(
    '/api/accreditations/audit-programmes',
    { method: 'POST', body: payload },
  );
}

export async function updateAuditProgramme(
  id: string,
  payload: { notes?: string | null; notApplicableGroups?: string[] },
): Promise<AuditProgramme> {
  return apiFetch<AuditProgramme>(
    `/api/accreditations/audit-programmes/${id}`,
    { method: 'PATCH', body: payload },
  );
}

export async function closeAndOpenNextAuditProgramme(
  id: string,
): Promise<AuditProgramme> {
  return apiFetch<AuditProgramme>(
    `/api/accreditations/audit-programmes/${id}/close-open-next`,
    { method: 'POST', body: {} },
  );
}

export async function reopenAuditProgramme(
  id: string,
): Promise<AuditProgramme> {
  return apiFetch<AuditProgramme>(
    `/api/accreditations/audit-programmes/${id}/reopen`,
    { method: 'POST', body: {} },
  );
}

/* ------------------------------------------------------------------ */
/*  ISO 9001 Cl. 6.2 — Quality Objectives                              */
/* ------------------------------------------------------------------ */

export interface QualityObjectiveProgress {
  id: string;
  objectiveId: string;
  entryDate: string;
  value: string;
  note: string | null;
  createdAt: string;
}

/** A person on an objective: the employee picked, or what was typed before people were picked. */
export interface ObjectivePerson {
  employeeId: string | null;
  name: string | null;
  /** The picked employee has left: the objective wants a new person. */
  left: boolean;
  /** Text typed before people were picked, shown until somebody is. */
  typed: string | null;
}

export type ObjectiveCommunicationRoute = 'meeting' | 'document' | 'memo' | 'policy';
/** A planned meeting or an unpublished memo is linked but has not yet filed 6.2. */
export type ObjectiveCommunicationState = 'communicated' | 'planned' | 'awaiting_publish' | 'approved';
export type ObjectivePlanPart = 'what' | 'resources' | 'who' | 'when' | 'how_often' | 'how_evaluated';

export interface ObjectiveCommunication {
  id: string;
  route: ObjectiveCommunicationRoute;
  targetId: string;
  title: string;
  communicatedOn: string;
  linkedAt: string;
  state: ObjectiveCommunicationState;
  /** The meeting it went out at, when added as a meeting was signed off; listed on that meeting's record. */
  viaMeetingId: string | null;
  /** What was recorded: the record an auditor asks to see. Null for a document or policy. */
  details: ObjectiveCommunicationDetails | null;
}
export interface ObjectiveCommunicationDetails {
  agenda?: string | null;
  minutes?: string | null;
  others?: string | null;
  /** A meeting: who was there; a memo: who it is addressed to. Only the people chosen. */
  people: string[];
  /** The same people as employee ids: what "Mark held" starts from. */
  peopleIds: string[];
  /** A memo: who it is for ('all', 'role' or 'individuals'), and the roles for 'role'. */
  audience?: string;
  roles?: string[];
}

export interface QualityObjective {
  id: string;
  /** OBJ-2026-001, from the company's counter. */
  reference: string | null;
  title: string;
  description: string | null;
  target: string;
  baseline: string | null;
  unit: string | null;
  /** Where the measured figure comes from. */
  sourceOfData: string | null;
  owner: ObjectivePerson;
  startDate: string | null;
  dueDate: string | null;
  status: string;
  planWhat: string | null;
  planResources: string | null;
  planWho: ObjectivePerson;
  planWhen: string | null;
  planHowEvaluated: string | null;
  /** 9.1.1: how often it is measured. */
  measureEvery: MeasureEvery | null;
  /** The standard it is for; its 6.2 and 9.1.1 evidence go there. */
  standard: IsoStandard;
  /** While active with a frequency set: the date the next measurement is due by. */
  nextMeasurementDue: string | null;
  measurementOverdue: boolean;
  activatedAt: string | null;
  createdAt: string;
  /** What activating still needs; empty when it can be activated. The server decides. */
  planMissing: ObjectivePlanPart[];
  progress: QualityObjectiveProgress[];
  communications: ObjectiveCommunication[];
  /** Entries on the Risks & Opportunities register linked to it; the link is made there. */
  risks: { id: string; title: string }[];
}
export interface QualityObjectiveListItem {
  id: string;
  reference: string | null;
  title: string;
  target: string;
  standard: IsoStandard;
  owner: string | null;
  ownerLeft: boolean;
  dueDate: string | null;
  status: string;
  activatedAt: string | null;
  progressCount: number;
  latestValue: string | null;
  communicationCount: number;
  nextMeasurementDue: string | null;
  measurementOverdue: boolean;
}

export interface ObjectiveCommunicationOption {
  id: string;
  title: string;
  date: string;
}
export type ObjectiveCommunicationOptions = Record<ObjectiveCommunicationRoute, ObjectiveCommunicationOption[]>;

export interface QualityObjectivePayload {
  title?: string;
  description?: string | null;
  target?: string;
  baseline?: string | null;
  unit?: string | null;
  sourceOfData?: string | null;
  ownerEmployeeId?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  planWhat?: string | null;
  planResources?: string | null;
  planWhoEmployeeId?: string | null;
  planWhen?: string | null;
  planHowEvaluated?: string | null;
  measureEvery?: MeasureEvery | null;
  /** A standard the company holds. Changing it moves the objective and re-files its evidence. */
  standard?: IsoStandard;
}

// ── ISO 9001 6.3 planned changes ────────────────────────────────────────

export type QualityChangeStatus = 'planned' | 'done' | 'cancelled';
export type QualityChangePart = 'purpose' | 'consequences' | 'integrity' | 'resources' | 'technical_skills' | 'impact' | 'responsible' | 'target_date';

export interface QualityChange {
  id: string;
  title: string;
  purpose: string | null;
  consequences: string | null;
  integrity: string | null;
  resources: string | null;
  technicalSkills: string | null;
  impact: string | null;
  /** Entries on the Risks & Opportunities register. */
  risks: { id: string; title: string }[];
  /** Null until a director or manager has decided. */
  approval: {
    decision: 'approved' | 'not_approved';
    reason: string;
    by: string | null;
    position: string | null;
    at: string | null;
  } | null;
  doneBy: { name: string | null; position: string | null };
  responsible: { employeeId: string | null; name: string | null; left: boolean };
  /** Everyone else working on it, and what each will do. */
  people: { employeeId: string; name: string; left: boolean; role: string }[];
  targetDate: string | null;
  status: QualityChangeStatus;
  doneOn: string | null;
  /** Planned, and its target day has passed. */
  overdue: boolean;
  /** What the plan still lacks before it can be marked done. */
  missing: QualityChangePart[];
  createdAt: string;
  updatedAt: string;
}

export interface QualityChangePayload {
  title?: string;
  purpose?: string | null;
  consequences?: string | null;
  integrity?: string | null;
  resources?: string | null;
  technicalSkills?: string | null;
  impact?: string | null;
  riskIds?: string[];
  responsibleEmployeeId?: string | null;
  targetDate?: string | null;
  people?: { employeeId: string; role: string }[];
}

export async function fetchQualityChanges(signal?: AbortSignal): Promise<QualityChange[]> {
  return apiFetch<QualityChange[]>('/api/accreditations/quality-changes', { method: 'GET', signal });
}

export async function createQualityChange(payload: QualityChangePayload & { title: string }): Promise<QualityChange> {
  return apiFetch<QualityChange>('/api/accreditations/quality-changes', { method: 'POST', body: payload });
}

export async function updateQualityChange(id: string, payload: QualityChangePayload): Promise<QualityChange> {
  return apiFetch<QualityChange>(`/api/accreditations/quality-changes/${id}`, { method: 'PUT', body: payload });
}

/** The change was carried out: files ISO 9001 6.3. */
export async function markQualityChangeDone(id: string, doneOn: string): Promise<QualityChange> {
  return apiFetch<QualityChange>(`/api/accreditations/quality-changes/${id}/done`, { method: 'POST', body: { doneOn } });
}

/** Kept, marked cancelled; any 6.3 it filed is withdrawn. */
export async function decideQualityChange(id: string, decision: 'approved' | 'not_approved', reason: string): Promise<QualityChange> {
  return apiFetch<QualityChange>(`/api/accreditations/quality-changes/${id}/decision`, { method: 'POST', body: { decision, reason } });
}

export async function cancelQualityChange(id: string): Promise<QualityChange> {
  return apiFetch<QualityChange>(`/api/accreditations/quality-changes/${id}/cancel`, { method: 'POST' });
}

export async function fetchQualityObjectives(
  signal?: AbortSignal,
): Promise<QualityObjectiveListItem[]> {
  return apiFetch<QualityObjectiveListItem[]>(
    '/api/accreditations/quality-objectives',
    { method: 'GET', signal },
  );
}

export async function fetchQualityObjective(
  id: string,
  signal?: AbortSignal,
): Promise<QualityObjective> {
  return apiFetch<QualityObjective>(
    `/api/accreditations/quality-objectives/${id}`,
    { method: 'GET', signal },
  );
}

export async function createQualityObjective(
  payload: QualityObjectivePayload & { title: string; target: string },
): Promise<QualityObjective> {
  return apiFetch<QualityObjective>(
    '/api/accreditations/quality-objectives',
    { method: 'POST', body: payload },
  );
}

export async function updateQualityObjective(
  id: string,
  payload: QualityObjectivePayload & { status?: 'met' | 'missed' | 'cancelled' },
): Promise<QualityObjective> {
  return apiFetch<QualityObjective>(
    `/api/accreditations/quality-objectives/${id}`,
    { method: 'PATCH', body: payload },
  );
}

export async function activateQualityObjective(
  id: string,
): Promise<QualityObjective> {
  return apiFetch<QualityObjective>(
    `/api/accreditations/quality-objectives/${id}/activate`,
    { method: 'POST', body: {} },
  );
}

/** What can carry an objective: finalised meetings, approved documents, published memos and policies. */
export async function fetchObjectiveCommunicationOptions(
  signal?: AbortSignal,
): Promise<ObjectiveCommunicationOptions> {
  return apiFetch<ObjectiveCommunicationOptions>(
    '/api/accreditations/quality-objectives/communication-options',
    { method: 'GET', signal },
  );
}

/** Record how an active objective was communicated. This files ISO 9001 6.2. */
export async function linkObjectiveCommunication(
  objectiveId: string,
  payload: { route: ObjectiveCommunicationRoute; targetId: string },
): Promise<QualityObjective> {
  return apiFetch<QualityObjective>(
    `/api/accreditations/quality-objectives/${objectiveId}/communications`,
    { method: 'POST', body: payload },
  );
}

/** A document, memo or policy that went out at a meeting, added as it is signed off. */
export interface ObjectiveMeetingMaterial {
  route: 'document' | 'memo' | 'policy';
  targetId: string;
}

/** Record a meeting from the objective, into the meetings register. Held files 6.2 now; planned when it is finalised. */
export async function recordObjectiveMeeting(
  objectiveId: string,
  payload: {
    title: string;
    meetingDate: string;
    agenda: string;
    attendeeEmployeeIds: string[];
    otherAttendees?: string | null;
    minutes?: string | null;
    held: boolean;
    materials?: ObjectiveMeetingMaterial[];
  },
): Promise<QualityObjective> {
  return apiFetch<QualityObjective>(
    `/api/accreditations/quality-objectives/${objectiveId}/communications/meeting`,
    { method: 'POST', body: payload },
  );
}

/** A planned meeting was held: the planned record carried in and brought up to date, then finalised (files 6.2). */
export async function holdObjectiveMeeting(
  objectiveId: string,
  communicationId: string,
  payload: {
    meetingDate: string;
    agenda: string;
    attendeeEmployeeIds: string[];
    otherAttendees?: string | null;
    minutes?: string | null;
    materials?: ObjectiveMeetingMaterial[];
  },
): Promise<QualityObjective> {
  return apiFetch<QualityObjective>(
    `/api/accreditations/quality-objectives/${objectiveId}/communications/${communicationId}/held`,
    { method: 'POST', body: payload },
  );
}

/** Mark a communication removed; its 6.2 evidence is withdrawn. */
export async function removeObjectiveCommunication(
  objectiveId: string,
  communicationId: string,
): Promise<QualityObjective> {
  return apiFetch<QualityObjective>(
    `/api/accreditations/quality-objectives/${objectiveId}/communications/${communicationId}`,
    { method: 'DELETE' },
  );
}

export async function addObjectiveProgress(
  objectiveId: string,
  payload: { entryDate: string; value: string; note?: string | null },
): Promise<QualityObjectiveProgress> {
  return apiFetch<QualityObjectiveProgress>(
    `/api/accreditations/quality-objectives/${objectiveId}/progress`,
    { method: 'POST', body: payload },
  );
}

export async function updateObjectiveProgress(
  progressId: string,
  payload: { entryDate?: string; value?: string; note?: string | null },
): Promise<QualityObjectiveProgress> {
  return apiFetch<QualityObjectiveProgress>(
    `/api/accreditations/quality-objective-progress/${progressId}`,
    { method: 'PATCH', body: payload },
  );
}

/** Remove a measurement entered in error; its 9.1.1 evidence is withdrawn. */
export async function deleteObjectiveProgress(progressId: string): Promise<{ id: string; evidenceWithdrawn: number }> {
  return apiFetch<{ id: string; evidenceWithdrawn: number }>(
    `/api/accreditations/quality-objective-progress/${progressId}`,
    { method: 'DELETE' },
  );
}

/* ------------------------------------------------------------------ */
/*  ISO 9001 Cl. 10.2 — Corrective Action (CAPA)                       */
/* ------------------------------------------------------------------ */

export interface CapaAction {
  id: string;
  capaId: string;
  description: string;
  owner: string | null;
  dueDate: string | null;
  status: string;
  completedAt: string | null;
  createdAt: string;
}
export interface Capa {
  id: string;
  reference: string | null;
  /** Null for an action covering every standard held. */
  standard: 'iso_9001' | 'iso_14001' | null;
  clause: string | null;
  closedBy: string | null;
  title: string;
  description: string;
  source: string | null;
  /** The procedure the nonconformity is against. */
  procedureReference: string | null;
  processMapStep: string | null;
  sourceType: string | null;
  sourceFindingId: string | null;
  /** The complaint it was raised from, whose form opens from the record. */
  complaintId?: string | null;
  /** Who raised it, by name. */
  raisedBy?: string | null;
  /** The complaint's customer where there is one. */
  productCustomerName?: string | null;
  severity: string | null;
  immediateCorrection: string | null;
  rootCause: string | null;
  /** 10.2.1(b3) whether similar nonconformities exist or could occur. */
  similarNonconformities: string | null;
  /** 10.2.1(e, f) risks, opportunities and the system: what was updated, or why nothing needed to be. */
  systemUpdates: string | null;
  effectivenessReview: string | null;
  effectivenessAt: string | null;
  productPartNumber: string | null;
  productName: string | null;
  productCustomerId: string | null;
  productQuantityReceived: number | null;
  productQuantityRejected: number | null;
  /** The server's sum. */
  defectPercent: number | null;
  productManufacturedOn: string | null;
  productSamples: string | null;
  productBatch: string | null;
  productFoundIn: CapaFoundIn | null;
  involvedAssembly: string | null;
  involvedProduction: string | null;
  involvedInspectedBy: string | null;
  involvedQuality: string | null;
  involvedSupplier: string | null;
  owner: string | null;
  dueDate: string | null;
  status: string;
  closedAt: string | null;
  createdAt: string;
  actions: CapaAction[];
  /** What closing still needs (ISO 10.2.1); empty when it can be closed. The server decides. */
  closeMissing: CapaPart[];
}
export type CapaPart = 'product' | 'correction' | 'cause' | 'similar' | 'system' | 'actions' | 'effectiveness';
export interface CapaListItem {
  id: string;
  reference: string | null;
  standard: 'iso_9001' | 'iso_14001' | null;
  clause: string | null;
  createdAt: string;
  closedBy: string | null;
  title: string;
  /** customer: from a complaint; supplier: about a supplier; internal: audit or raised by hand. */
  register: 'customer' | 'internal' | 'supplier';
  source: string | null;
  procedureReference: string | null;
  processMapStep: string | null;
  severity: string | null;
  owner: string | null;
  dueDate: string | null;
  status: string;
  closedAt: string | null;
  openActionCount: number;
  totalActionCount: number;
}

export async function fetchCapas(
  signal?: AbortSignal,
): Promise<CapaListItem[]> {
  return apiFetch<CapaListItem[]>('/api/accreditations/capas', {
    method: 'GET',
    signal,
  });
}

export async function fetchCapa(
  id: string,
  signal?: AbortSignal,
): Promise<Capa> {
  return apiFetch<Capa>(`/api/accreditations/capas/${id}`, {
    method: 'GET',
    signal,
  });
}

export async function createCapa(payload: {
  title: string;
  description: string;
  standard?: 'iso_9001' | 'iso_14001' | null;
  clause?: string | null;
  source?: string | null;
  sourceType?: string | null;
  sourceFindingId?: string | null;
  severity?: string | null;
  immediateCorrection?: string | null;
  rootCause?: string | null;
  owner?: string | null;
  dueDate?: string | null;
  /** A complaint raised here is logged against this customer too. */
  customerId?: string;
  receivedOn?: string;
  channel?: string | null;
}): Promise<Capa> {
  return apiFetch<Capa>('/api/accreditations/capas', {
    method: 'POST',
    body: payload,
  });
}

export async function updateCapa(
  id: string,
  payload: {
    standard?: 'iso_9001' | 'iso_14001' | null;
    clause?: string | null;
    title?: string;
    description?: string;
    source?: string | null;
    procedureReference?: string | null;
    processMapStep?: string | null;
    severity?: string | null;
    immediateCorrection?: string | null;
    rootCause?: string | null;
    similarNonconformities?: string | null;
    systemUpdates?: string | null;
    effectivenessReview?: string | null;
    effectivenessAt?: string | null;
    owner?: string | null;
    dueDate?: string | null;
    status?: 'open' | 'action' | 'review' | 'cancelled';
    productPartNumber?: string | null;
    productName?: string | null;
    productCustomerId?: string | null;
    productQuantityReceived?: number | null;
    productQuantityRejected?: number | null;
    productManufacturedOn?: string | null;
    productSamples?: string | null;
    productBatch?: string | null;
    productFoundIn?: CapaFoundIn | null;
    involvedAssembly?: string | null;
    involvedProduction?: string | null;
    involvedInspectedBy?: string | null;
    involvedQuality?: string | null;
    involvedSupplier?: string | null;
  },
): Promise<Capa> {
  return apiFetch<Capa>(`/api/accreditations/capas/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function closeCapa(id: string): Promise<Capa> {
  return apiFetch<Capa>(`/api/accreditations/capas/${id}/close`, {
    method: 'POST',
    body: {},
  });
}

/**
 * Reopen a closed nonconformity (director/manager).
 *
 * Closing locked the record permanently — every control renders only while
 * unlocked and `updateCapa` refuses anything closed — so a CAPA closed by
 * mistake, or one whose effectiveness review later fails, had no way back.
 */
export async function reopenCapa(id: string): Promise<Capa> {
  return apiFetch<Capa>(`/api/accreditations/capas/${id}/reopen`, {
    method: 'POST',
    body: {},
  });
}

export async function addCapaAction(
  capaId: string,
  payload: { description: string; owner?: string | null; dueDate?: string | null },
): Promise<CapaAction> {
  return apiFetch<CapaAction>(
    `/api/accreditations/capas/${capaId}/actions`,
    { method: 'POST', body: payload },
  );
}

export async function updateCapaAction(
  actionId: string,
  payload: {
    description?: string;
    owner?: string | null;
    dueDate?: string | null;
    status?: 'open' | 'done';
  },
): Promise<CapaAction> {
  return apiFetch<CapaAction>(
    `/api/accreditations/capa-actions/${actionId}`,
    { method: 'PATCH', body: payload },
  );
}

/* ------------------------------------------------------------------ */
/*  ISO 9001 Cl. 4.1/4.2 — Context of the organisation                 */
/* ------------------------------------------------------------------ */

export interface ContextIssue {
  id: string;
  reviewId: string;
  issueType: string;
  category: string | null;
  description: string;
  impact: string | null;
  createdAt: string;
}
export interface ContextParty {
  id: string;
  reviewId: string;
  name: string;
  partyType: string | null;
  requirements: string;
  howMonitored: string | null;
  createdAt: string;
}
export interface ContextReview {
  id: string;
  title: string;
  reviewDate: string;
  periodLabel: string | null;
  facilitator: string | null;
  summary: string | null;
  status: string;
  finalisedAt: string | null;
  createdAt: string;
  issues: ContextIssue[];
  parties: ContextParty[];
}
export interface ContextReviewListItem {
  id: string;
  title: string;
  reviewDate: string;
  periodLabel: string | null;
  status: string;
  finalisedAt: string | null;
  issueCount: number;
  partyCount: number;
}

export async function fetchContextReviews(
  signal?: AbortSignal,
): Promise<ContextReviewListItem[]> {
  return apiFetch<ContextReviewListItem[]>(
    '/api/accreditations/context-reviews',
    { method: 'GET', signal },
  );
}

export async function fetchContextReview(
  id: string,
  signal?: AbortSignal,
): Promise<ContextReview> {
  return apiFetch<ContextReview>(
    `/api/accreditations/context-reviews/${id}`,
    { method: 'GET', signal },
  );
}

export async function createContextReview(payload: {
  title: string;
  reviewDate: string;
  periodLabel?: string | null;
  facilitator?: string | null;
  summary?: string | null;
}): Promise<ContextReview> {
  return apiFetch<ContextReview>('/api/accreditations/context-reviews', {
    method: 'POST',
    body: payload,
  });
}

export async function updateContextReview(
  id: string,
  payload: {
    title?: string;
    reviewDate?: string;
    periodLabel?: string | null;
    facilitator?: string | null;
    summary?: string | null;
  },
): Promise<ContextReview> {
  return apiFetch<ContextReview>(
    `/api/accreditations/context-reviews/${id}`,
    { method: 'PATCH', body: payload },
  );
}

export async function finaliseContextReview(
  id: string,
): Promise<ContextReview> {
  return apiFetch<ContextReview>(
    `/api/accreditations/context-reviews/${id}/finalise`,
    { method: 'POST', body: {} },
  );
}

export async function addContextIssue(
  reviewId: string,
  payload: {
    issueType?: 'internal' | 'external';
    category?: string | null;
    description: string;
    impact?: string | null;
  },
): Promise<ContextIssue> {
  return apiFetch<ContextIssue>(
    `/api/accreditations/context-reviews/${reviewId}/issues`,
    { method: 'POST', body: payload },
  );
}

export async function updateContextIssue(
  issueId: string,
  payload: {
    issueType?: 'internal' | 'external';
    category?: string | null;
    description?: string;
    impact?: string | null;
  },
): Promise<ContextIssue> {
  return apiFetch<ContextIssue>(
    `/api/accreditations/context-issues/${issueId}`,
    { method: 'PATCH', body: payload },
  );
}

export async function addInterestedParty(
  reviewId: string,
  payload: {
    name: string;
    partyType?: 'internal' | 'external' | null;
    requirements: string;
    howMonitored?: string | null;
  },
): Promise<ContextParty> {
  return apiFetch<ContextParty>(
    `/api/accreditations/context-reviews/${reviewId}/parties`,
    { method: 'POST', body: payload },
  );
}

export async function updateInterestedParty(
  partyId: string,
  payload: {
    name?: string;
    partyType?: 'internal' | 'external' | null;
    requirements?: string;
    howMonitored?: string | null;
  },
): Promise<ContextParty> {
  return apiFetch<ContextParty>(
    `/api/accreditations/context-parties/${partyId}`,
    { method: 'PATCH', body: payload },
  );
}

/* ------------------------------------------------------------------ */
/*  Communication — Meetings + ISO 7.4 matrix                          */
/* ------------------------------------------------------------------ */

export interface MeetingAction {
  id: string;
  meetingId: string;
  description: string;
  owner: string | null;
  dueDate: string | null;
  status: string;
  completedAt: string | null;
  createdAt: string;
}
/** An employee present at a meeting. */
export interface MeetingAttendee {
  employeeId: string;
  name: string;
  left: boolean;
}
export interface Meeting {
  id: string;
  title: string;
  meetingType: string;
  meetingDate: string;
  facilitator: string | null;
  /** Others present who are not employees; on older meetings, everyone present. */
  attendees: string | null;
  /** The employees present, from the employee list. */
  attendeeEmployees: MeetingAttendee[];
  agenda: string | null;
  minutes: string | null;
  decisions: string | null;
  status: string;
  finalisedAt: string | null;
  createdAt: string;
  actions: MeetingAction[];
}
export interface MeetingListItem {
  id: string;
  title: string;
  meetingType: string;
  meetingDate: string;
  status: string;
  finalisedAt: string | null;
  openActionCount: number;
  totalActionCount: number;
}
export interface CommunicationItem {
  id: string;
  topic: string;
  direction: string;
  audience: string | null;
  method: string | null;
  frequency: string | null;
  responsible: string | null;
  notes: string | null;
  status: string;
  createdAt: string;
}

export async function fetchMeetings(
  signal?: AbortSignal,
): Promise<MeetingListItem[]> {
  return apiFetch<MeetingListItem[]>('/api/accreditations/meetings', {
    method: 'GET',
    signal,
  });
}

export async function fetchMeeting(
  id: string,
  signal?: AbortSignal,
): Promise<Meeting> {
  return apiFetch<Meeting>(`/api/accreditations/meetings/${id}`, {
    method: 'GET',
    signal,
  });
}

export async function createMeeting(payload: {
  title: string;
  meetingType?: 'management' | 'production' | 'other';
  meetingDate: string;
  facilitator?: string | null;
  attendees?: string | null;
  attendeeEmployeeIds?: string[];
  agenda?: string | null;
}): Promise<Meeting> {
  return apiFetch<Meeting>('/api/accreditations/meetings', {
    method: 'POST',
    body: payload,
  });
}

export async function updateMeeting(
  id: string,
  payload: {
    title?: string;
    meetingType?: 'management' | 'production' | 'other';
    meetingDate?: string;
    facilitator?: string | null;
    attendees?: string | null;
    agenda?: string | null;
    minutes?: string | null;
    decisions?: string | null;
    attendeeEmployeeIds?: string[];
  },
): Promise<Meeting> {
  return apiFetch<Meeting>(`/api/accreditations/meetings/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function finaliseMeeting(id: string): Promise<Meeting> {
  return apiFetch<Meeting>(
    `/api/accreditations/meetings/${id}/finalise`,
    { method: 'POST', body: {} },
  );
}

export async function addMeetingAction(
  meetingId: string,
  payload: { description: string; owner?: string | null; dueDate?: string | null },
): Promise<MeetingAction> {
  return apiFetch<MeetingAction>(
    `/api/accreditations/meetings/${meetingId}/actions`,
    { method: 'POST', body: payload },
  );
}

export async function updateMeetingAction(
  actionId: string,
  payload: {
    description?: string;
    owner?: string | null;
    dueDate?: string | null;
    status?: 'open' | 'done';
  },
): Promise<MeetingAction> {
  return apiFetch<MeetingAction>(
    `/api/accreditations/meeting-actions/${actionId}`,
    { method: 'PATCH', body: payload },
  );
}

export async function fetchCommunicationItems(
  signal?: AbortSignal,
): Promise<CommunicationItem[]> {
  return apiFetch<CommunicationItem[]>(
    '/api/accreditations/communication-items',
    { method: 'GET', signal },
  );
}

export async function createCommunicationItem(payload: {
  topic: string;
  direction?: 'internal' | 'external' | 'both';
  audience?: string | null;
  method?: string | null;
  frequency?: string | null;
  responsible?: string | null;
  notes?: string | null;
}): Promise<CommunicationItem> {
  return apiFetch<CommunicationItem>(
    '/api/accreditations/communication-items',
    { method: 'POST', body: payload },
  );
}

export async function updateCommunicationItem(
  id: string,
  payload: {
    topic?: string;
    direction?: 'internal' | 'external' | 'both';
    audience?: string | null;
    method?: string | null;
    frequency?: string | null;
    responsible?: string | null;
    notes?: string | null;
    status?: 'active' | 'archived';
  },
): Promise<CommunicationItem> {
  return apiFetch<CommunicationItem>(
    `/api/accreditations/communication-items/${id}`,
    { method: 'PATCH', body: payload },
  );
}

/* ------------------------------------------------------------------ */
/*  Sidebar badge                                                      */
/* ------------------------------------------------------------------ */

export async function fetchExpiringCount(signal?: AbortSignal): Promise<number> {
  const data = await apiFetch<{ count: number }>('/api/accreditations/expiring-count', {
    method: 'GET',
    signal,
  });
  return data.count;
}

// ---------------------------------------------------------------------------
// ISO 9001 clause 6.1 — the risks and opportunities register.
// Server: apps/api/src/services/accreditations/riskOpportunityService.ts, which
// owns the scoring (1-5 likelihood x 1-5 impact) and returns score and band, so
// the screen shows them and never works them out itself.
// ---------------------------------------------------------------------------

export type RiskKind = 'risk' | 'opportunity';
export type RiskStatus = 'open' | 'actioned' | 'closed';
export type RiskSourceType = 'context_issue' | 'interested_party' | 'other';
export type RiskBand = 'low' | 'medium' | 'high';
export type ReviewEffective = 'yes' | 'partly' | 'no';

export interface RiskReview {
  id: string;
  reviewDate: string;
  effective: ReviewEffective;
  note: string;
  residualLikelihood: number | null;
  residualImpact: number | null;
  residualScore: number | null;
  residualBand: RiskBand | null;
  newActions: string | null;
  newActionOwner: string | null;
  newActionDue: string | null;
  reviewedById: string;
  createdAt: string;
}

export interface RiskOpportunity {
  id: string;
  kind: RiskKind;
  title: string;
  description: string | null;
  sourceType: RiskSourceType;
  sourceId: string | null;
  sourceNote: string | null;
  likelihood: number | null;
  impact: number | null;
  score: number | null;
  band: RiskBand | null;
  benefit: string | null;
  action: string | null;
  owner: string | null;
  dueDate: string | null;
  status: RiskStatus;
  /** Null for every standard held. */
  standard: IsoStandard | null;
  keyArea: string | null;
  existingControls: string | null;
  actionsImplemented: boolean | null;
  objectives: { id: string; reference: string | null; title: string }[];
  createdAt: string;
  updatedAt: string;
  /** Newest first. */
  reviews: RiskReview[];
}

export interface RiskOpportunityPayload {
  kind: RiskKind;
  title: string;
  description?: string | null;
  sourceType?: RiskSourceType;
  sourceId?: string | null;
  sourceNote?: string | null;
  likelihood?: number | null;
  impact?: number | null;
  benefit?: string | null;
  action?: string | null;
  owner?: string | null;
  dueDate?: string | null;
  status?: RiskStatus;
  standard?: IsoStandard | null;
  keyArea?: string | null;
  existingControls?: string | null;
  actionsImplemented?: boolean | null;
  objectiveIds?: string[];
}

export interface RiskReviewPayload {
  reviewDate: string;
  effective: ReviewEffective;
  note: string;
  residualLikelihood?: number | null;
  residualImpact?: number | null;
  newActions?: string | null;
  newActionOwner?: string | null;
  newActionDue?: string | null;
  close?: boolean;
}

export async function fetchRiskOpportunities(signal?: AbortSignal): Promise<RiskOpportunity[]> {
  return apiFetch<RiskOpportunity[]>('/api/accreditations/risks-opportunities', { method: 'GET', signal });
}

export async function createRiskOpportunity(payload: RiskOpportunityPayload): Promise<RiskOpportunity> {
  return apiFetch<RiskOpportunity>('/api/accreditations/risks-opportunities', {
    method: 'POST',
    body: payload,
  });
}

export async function updateRiskOpportunity(
  id: string,
  payload: Partial<RiskOpportunityPayload>,
): Promise<RiskOpportunity> {
  return apiFetch<RiskOpportunity>(`/api/accreditations/risks-opportunities/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function archiveRiskOpportunity(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/api/accreditations/risks-opportunities/${id}`, { method: 'DELETE' });
}

/** Files ISO 9001 clause 6.1 evidence on the server. */
export async function recordRiskReview(id: string, payload: RiskReviewPayload): Promise<RiskOpportunity> {
  return apiFetch<RiskOpportunity>(`/api/accreditations/risks-opportunities/${id}/reviews`, {
    method: 'POST',
    body: payload,
  });
}

// ---------------------------------------------------------------------------
// ISO 14001 clause 6.1.2 — the environmental aspects register, on the risk
// register's scale. Server: environmentalAspectService.ts returns score and band.
// ---------------------------------------------------------------------------

export type AspectCondition = 'normal' | 'abnormal' | 'emergency';

export interface AspectReview {
  id: string;
  reviewDate: string;
  effective: ReviewEffective;
  note: string;
  residualLikelihood: number | null;
  residualConsequence: number | null;
  residualScore: number | null;
  residualBand: RiskBand | null;
  reviewedById: string;
  createdAt: string;
}

export interface EnvironmentalAspect {
  id: string;
  activity: string | null;
  aspect: string;
  impact: string;
  condition: AspectCondition;
  likelihood: number;
  consequence: number;
  score: number;
  band: RiskBand;
  significant: boolean;
  objective: string | null;
  target: string | null;
  targetDate: string | null;
  control: string | null;
  kpi: string | null;
  owner: string | null;
  status: RiskStatus;
  obligations: Array<{ id: string; title: string; retired: boolean }>;
  createdAt: string;
  updatedAt: string;
  /** Newest first. */
  reviews: AspectReview[];
}

export interface EnvironmentalAspectPayload {
  activity?: string | null;
  aspect: string;
  impact: string;
  condition?: AspectCondition;
  likelihood: number;
  consequence: number;
  significant?: boolean;
  objective?: string | null;
  target?: string | null;
  targetDate?: string | null;
  control?: string | null;
  kpi?: string | null;
  owner?: string | null;
  status?: RiskStatus;
  obligationIds?: string[];
}

export interface AspectReviewPayload {
  reviewDate: string;
  effective: ReviewEffective;
  note: string;
  residualLikelihood?: number | null;
  residualConsequence?: number | null;
  close?: boolean;
}

export async function fetchEnvironmentalAspects(signal?: AbortSignal): Promise<EnvironmentalAspect[]> {
  return apiFetch<EnvironmentalAspect[]>('/api/accreditations/environmental-aspects', { method: 'GET', signal });
}

export async function createEnvironmentalAspect(payload: EnvironmentalAspectPayload): Promise<EnvironmentalAspect> {
  return apiFetch<EnvironmentalAspect>('/api/accreditations/environmental-aspects', {
    method: 'POST',
    body: payload,
  });
}

export async function updateEnvironmentalAspect(
  id: string,
  payload: Partial<EnvironmentalAspectPayload>,
): Promise<EnvironmentalAspect> {
  return apiFetch<EnvironmentalAspect>(`/api/accreditations/environmental-aspects/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function archiveEnvironmentalAspect(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/api/accreditations/environmental-aspects/${id}`, { method: 'DELETE' });
}

/** Files ISO 14001 clause 6.1.2 evidence on the server. */
export async function recordAspectReview(id: string, payload: AspectReviewPayload): Promise<EnvironmentalAspect> {
  return apiFetch<EnvironmentalAspect>(`/api/accreditations/environmental-aspects/${id}/reviews`, {
    method: 'POST',
    body: payload,
  });
}

// ---------------------------------------------------------------------------
// ISO 14001 clause 9.1.1 — environmental figures. Server:
// environmentalFiguresService.ts works out every total and the CO2e.
// ---------------------------------------------------------------------------

/** Electricity, gas, water, or a supply type's key (supply_lpg). */
export type Utility = string;
export type MeterUnit = SupplyUnit;
export type ReadingSource = 'typed' | 'bill' | 'bill_read';
export type BillReader = 'code' | 'jeanie';
export type EnvironmentalMeasure = 'electricity' | 'gas' | 'water' | 'fuel' | 'waste' | 'co2e';

export interface EnvironmentalMeter {
  id: string;
  siteId: string;
  siteName: string;
  utility: Utility;
  unit: MeterUnit;
  name: string;
  meterPoint: string | null;
  serialNumber: string | null;
  calibrationDue: string | null;
  calibrationOverdue: boolean;
}

export interface EnvironmentalMeterPayload {
  siteId: string;
  utility: Utility;
  unit: MeterUnit;
  name: string;
  meterPoint?: string | null;
  serialNumber?: string | null;
  calibrationDue?: string | null;
}

export interface EnvironmentalReading {
  id: string;
  meterId: string;
  periodMonth: string;
  quantity: number;
  cost: number | null;
  source: ReadingSource;
  bill: { id: string; reference: string; supplierName: string | null } | null;
  readBy: BillReader | null;
  fileName: string | null;
  notes: string | null;
}

export interface EnvironmentalReadingPayload {
  meterId: string;
  periodMonth: string;
  quantity: number;
  cost?: number | null;
  unit?: MeterUnit | null;
  source?: ReadingSource;
  supplierInvoiceId?: string | null;
  readBy?: BillReader | null;
  fileName?: string | null;
  notes?: string | null;
}

export interface EnvironmentalTotals {
  electricityKwh: number;
  gasKwh: number;
  gasM3: number;
  waterM3: number;
  fuelLitres: number;
  dieselLitres: number;
  petrolLitres: number;
  wasteTonnes: number;
  cost: number;
  co2eKg: number;
}

export interface EnvironmentalMonth extends EnvironmentalTotals {
  month: string;
  review: { id: string; note: string; reviewedById: string; createdAt: string } | null;
}

/** Where a measure stands against the year's target, at the rate so far (the server's working). */
export interface EnvironmentalStanding {
  measure: EnvironmentalMeasure;
  target: number;
  used: number;
  projected: number;
  /** Months from the measure's first figure to now; nought means no figures yet. */
  measuredMonths: number;
  status: 'on_target' | 'over';
  neededPerMonth: number | null;
  passed: boolean;
}

export interface EnvironmentalQuarter extends EnvironmentalTotals {
  quarter: 1 | 2 | 3 | 4;
  label: string;
  firstMonth: string;
  ended: boolean;
  standing: EnvironmentalStanding[];
  review: { id: string; note: string; reviewedById: string; reviewedByName: string | null; createdAt: string } | null;
  /** What Reviewed records, or what it recorded. */
  wording: string;
}

export interface EnvironmentalVehicleFuel {
  vehicleId: string;
  registration: string;
  fuelType: string | null;
  litres: number;
  cost: number;
  /** Null where the fuel type has no conversion factor. */
  co2eKg: number | null;
  lastYearLitres: number;
}

export interface EnvironmentalFigures {
  year: number;
  /** Null is every building, with fuel and waste; a building's figures are its meters only. */
  siteId: string | null;
  countryCode: string;
  factors: { year: number | null; source: string | null; missing: string[] };
  months: EnvironmentalMonth[];
  monthsGone: number;
  standing: EnvironmentalStanding[];
  quarters: EnvironmentalQuarter[];
  totals: EnvironmentalTotals;
  lastYear: EnvironmentalTotals;
  targets: Partial<Record<EnvironmentalMeasure, number>>;
  meters: EnvironmentalMeter[];
  readings: EnvironmentalReading[];
  fuelWithoutFactorLitres: number;
  wasteOtherUnits: Array<{ unit: string; quantity: number }>;
  vehicles: EnvironmentalVehicleFuel[];
  /** The country's supply types beyond electricity, gas and water. */
  supplyTypes: SupplyType[];
  otherSupplies: EnvironmentalOtherSupply[];
}

/** A supply type's year in its own unit; co2eKg null where the year holds no factor for it. */
export interface EnvironmentalOtherSupply extends SupplyType {
  quantity: number;
  cost: number;
  co2eKg: number | null;
  lastYearQuantity: number;
}

export interface BillForReading {
  id: string;
  reference: string;
  supplierName: string | null;
  invoiceDate: string;
  amount: number;
}

export interface BillReadResult {
  readBy: BillReader;
  fileName: string;
  complete: boolean;
  proposal: {
    meterId: string | null;
    utility: Utility | null;
    quantity: number | null;
    unit: MeterUnit | null;
    periodStart: string | null;
    periodEnd: string | null;
    periodMonth: string | null;
    meterPoint: string | null;
    cost: number | null;
    reference: string | null;
    supplierName: string | null;
  };
}

const FIGURES = '/api/accreditations/environmental-figures';

export async function fetchEnvironmentalFigures(
  year: number,
  siteId: string | null,
  signal?: AbortSignal,
): Promise<EnvironmentalFigures> {
  const query: Record<string, string> = { year: String(year) };
  if (siteId) query['siteId'] = siteId;
  return apiFetch<EnvironmentalFigures>(FIGURES, { method: 'GET', query, signal });
}

export async function fetchBillsForReadings(signal?: AbortSignal): Promise<BillForReading[]> {
  return apiFetch<BillForReading[]>(`${FIGURES}/bills`, { method: 'GET', signal });
}

export async function createEnvironmentalMeter(payload: EnvironmentalMeterPayload): Promise<EnvironmentalMeter> {
  return apiFetch<EnvironmentalMeter>(`${FIGURES}/meters`, { method: 'POST', body: payload });
}

export async function updateEnvironmentalMeter(id: string, payload: EnvironmentalMeterPayload): Promise<EnvironmentalMeter> {
  return apiFetch<EnvironmentalMeter>(`${FIGURES}/meters/${id}`, { method: 'PUT', body: payload });
}

export async function archiveEnvironmentalMeter(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`${FIGURES}/meters/${id}`, { method: 'DELETE' });
}

export async function saveEnvironmentalReading(payload: EnvironmentalReadingPayload): Promise<EnvironmentalReading> {
  return apiFetch<EnvironmentalReading>(`${FIGURES}/readings`, { method: 'POST', body: payload });
}

export async function setEnvironmentalTargets(
  year: number,
  targets: Array<{ measure: EnvironmentalMeasure; target: number | null }>,
): Promise<Partial<Record<EnvironmentalMeasure, number>>> {
  return apiFetch<Partial<Record<EnvironmentalMeasure, number>>>(`${FIGURES}/targets/${year}`, {
    method: 'PUT',
    body: { targets },
  });
}

/** Reviewed on an ended quarter: files ISO 14001 clause 9.1.1 naming who pressed it. */
export async function reviewEnvironmentalQuarter(year: number, quarter: number): Promise<{ id: string; note: string }> {
  return apiFetch<{ id: string; note: string }>(`${FIGURES}/quarters/${year}/${quarter}/review`, { method: 'POST' });
}

/**
 * Send a dropped bill or email to be read. Raw fetch, because a multipart body
 * needs the browser to set its own boundary. Nothing is saved by the server.
 */
export interface CertificateFileRead {
  readBy: 'text' | 'ocr' | 'ai' | 'none';
  /** False when no words could be got off the file, so a better copy would help. */
  wordsRead: boolean;
  fileName: string;
  complete: boolean;
  proposal: {
    type: AccreditationType | null;
    certificateNumber: string | null;
    issuedDate: string | null;
    expiryDate: string | null;
    certifyingBody: string | null;
  };
}

/** Read a certificate file; nothing is saved. The answer fills the filing form for the person to check. */
export async function readCertificateFile(file: File): Promise<CertificateFileRead> {
  const fd = new FormData();
  fd.append('file', file);
  const token = getAccessToken();
  const path = '/api/accreditations/read-certificate';
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  const body = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    data?: CertificateFileRead;
    error?: { code?: string; message?: string };
  };
  if (res.status === 401 && token) throw expiredSession();
  if (!res.ok || !body.success || !body.data) {
    throw new ApiError(
      body.error?.code ?? 'READ_FAILED',
      body.error?.message ?? 'The certificate could not be read',
      res.status,
      undefined,
      { method: 'POST', path },
    );
  }
  return body.data;
}

export async function readEnvironmentalBill(file: File, reader: BillReader): Promise<BillReadResult> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('reader', reader);
  const token = getAccessToken();
  const path = `${FIGURES}/read-bill`;
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  const body = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    data?: BillReadResult;
    error?: { code?: string; message?: string };
  };
  if (res.status === 401 && token) throw expiredSession();
  if (!res.ok || !body.success || !body.data) {
    throw new ApiError(
      body.error?.code ?? 'READ_FAILED',
      body.error?.message ?? 'The bill could not be read',
      res.status,
      undefined,
      { method: 'POST', path },
    );
  }
  return body.data;
}

// ── ISO 14001 8.2 Emergency Preparedness ─────────────────────────────────

/** The procedure: not yet in the library, in the library to add, or the company's own copy. */
export interface EmergencyProcedure {
  state: 'not_in_library' | 'not_on_register' | 'awaiting_approval' | 'approved';
  registerNumber: string | null;
  policy: { id: string; name: string; status: string; version: number; fieldsToComplete: string[] } | null;
}

export interface EmergencyResponseTest {
  id: string;
  siteId: string | null;
  siteName: string | null;
  testedOn: string;
  location: string | null;
  emergencyType: string;
  whatWasTested: string;
  carriedOutBy: string;
  findings: string | null;
  actions: string | null;
  recordedById: string;
}

export interface SpillKit {
  id: string;
  siteId: string;
  siteName: string | null;
  name: string;
  location: string;
  lastCheckDate: string | null;
  lastCheckOutcome: 'fully_stocked' | 'items_missing' | 'not_checked' | null;
  nextCheckDue: string | null;
  isOverdue: boolean;
}

export interface EmergencyPreparedness {
  procedure: EmergencyProcedure;
  emergencyAspects: EnvironmentalAspect[];
  spillKits: SpillKit[];
  drillBuildings: Array<{ siteId: string | null; siteName: string | null; lastHeldOn: string | null; nextDue: string | null; due: boolean }>;
  tests: EmergencyResponseTest[];
}

export interface EmergencyResponseTestPayload {
  siteId?: string | null;
  testedOn: string;
  location?: string | null;
  emergencyType: string;
  whatWasTested: string;
  carriedOutBy: string;
  findings?: string | null;
  actions?: string | null;
}

export async function fetchEmergencyPreparedness(signal?: AbortSignal): Promise<EmergencyPreparedness> {
  return apiFetch<EmergencyPreparedness>('/api/accreditations/emergency-preparedness', { method: 'GET', signal });
}

export async function recordEmergencyResponseTest(payload: EmergencyResponseTestPayload): Promise<EmergencyResponseTest> {
  return apiFetch<EmergencyResponseTest>('/api/accreditations/emergency-preparedness/tests', {
    method: 'POST',
    body: payload,
  });
}

export async function archiveEmergencyResponseTest(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/api/accreditations/emergency-preparedness/tests/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
